import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Trash2, Loader2 } from "lucide-react";
import { Pill } from "@/components/layout/PageShell";
import MasterCrudShell from "@/components/layout/MasterCrudShell";
import { DataToolbar, DataTable } from "@/components/layout/DataTable";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_GROUPS = ["Spice", "Veg", "Add-on", "Drink", "Cooking", "General"];

const EMPTY = {
  code: "",
  name: "",
  group: "General",
  sort_order: 0,
  status: "Active",
};

export default function KitchenNotesMaster() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const [view, setView] = useState("form");
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("All");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data, isLoading } = useQuery({
    queryKey: ["kitchen-notes", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/kitchen-notes", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId),
  });

  const notes = data?.kitchen_notes ?? [];
  const apiGroups = data?.groups ?? [];

  const groups = useMemo(
    () => ["All", ...Array.from(new Set([...DEFAULT_GROUPS, ...apiGroups]))],
    [apiGroups],
  );

  const filtered = useMemo(
    () =>
      notes.filter(
        (k) =>
          (group === "All" || k.group === group) &&
          (k.name.toLowerCase().includes(search.toLowerCase()) ||
            k.code.toLowerCase().includes(search.toLowerCase())),
      ),
    [notes, search, group],
  );

  function nextNoteCode() {
    let max = 0;
    notes.forEach((n) => {
      const m = String(n.code || "").match(/(\d+)\s*$/);
      if (m) max = Math.max(max, Number(m[1]));
    });
    return `KN-${String(max + 1).padStart(2, "0")}`;
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        code: form.code?.trim() || undefined,
        name: form.name?.trim(),
        group: form.group || "General",
        sort_order: Number(form.sort_order) || 0,
        status: form.status,
        is_active: form.status === "Active",
      };
      if (editingId) {
        return api.put(`/kitchen-notes/${editingId}`, payload, { params: { branch_id: branchId } });
      }
      return api.post("/kitchen-notes", payload, { params: { branch_id: branchId } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kitchen-notes", branchId] });
      toast.success(editingId ? "Kitchen note updated" : "Kitchen note created");
      setEditingId(null);
      setForm(EMPTY);
      setView("form");
    },
    onError: (err) => {
      const msg =
        err.response?.data?.message
        || err.response?.data?.errors?.code?.[0]
        || err.response?.data?.errors?.name?.[0]
        || "Failed to save kitchen note";
      toast.error(msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/kitchen-notes/${id}`, { params: { branch_id: branchId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kitchen-notes", branchId] });
      toast.success("Kitchen note deleted");
    },
    onError: () => toast.error("Failed to delete kitchen note"),
  });

  function openNew() {
    setEditingId(null);
    setForm({
      ...EMPTY,
      code: nextNoteCode(),
      group: group === "All" ? "General" : group,
    });
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm({
      code: row.code,
      name: row.name,
      group: row.group,
      sort_order: row.sort_order,
      status: row.status,
    });
    setView("form");
  }

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    if (!form.name?.trim()) {
      toast.error("Note is required.");
      return;
    }
    saveMutation.mutate();
  }

  return (
    <MasterCrudShell
      title="Kitchen Notes Master"
      subtitle={`${notes.length} modifiers — ${user?.default_branch?.name || "branch"}`}
      view={view}
      onViewChange={setView}
      isNew={!editingId}
      onSave={handleSave}
      saving={saveMutation.isPending}
      onAddNew={openNew}
      testIdPrefix="knotes"
      formTitle={editingId ? form.name || "Edit Kitchen Note" : "New Kitchen Note"}
      formSubtitle={editingId ? "Edit" : "Create"}
      list={
        <>
          <DataToolbar
            searchValue={search}
            onSearch={setSearch}
            testIdPrefix="knotes"
            extra={
              <Select value={group} onValueChange={setGroup}>
                <SelectTrigger className="w-[160px] h-9" data-testid="knotes-group">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((g) => (
                    <SelectItem key={g} value={g}>{g === "All" ? "All Groups" : g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />
          {isLoading ? (
            <div className="h-32 grid place-items-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <DataTable
              testIdPrefix="knotes"
              onRowClick={openEdit}
              columns={[
                { key: "code", label: "Code", mono: true },
                { key: "name", label: "Note", bold: true },
                { key: "group", label: "Group" },
                {
                  key: "status",
                  label: "Status",
                  render: (v) => <Pill tone={v === "Active" ? "green" : "default"}>{v}</Pill>,
                },
              ]}
              rows={filtered}
              rowActions={(row) => [
                { title: "Edit", icon: <Edit3 size={14} />, onClick: () => openEdit(row) },
                {
                  title: "Delete",
                  icon: <Trash2 size={14} />,
                  danger: true,
                  onClick: () => {
                    if (window.confirm(`Delete note "${row.name}"?`)) {
                      deleteMutation.mutate(row.id);
                    }
                  },
                },
              ]}
            />
          )}
        </>
      }
    >
      <div className="grid gap-3 max-w-md">
        <Field label="Code *">
          <Input
            value={form.code}
            onChange={(e) => setField("code", e.target.value)}
            className="h-9 font-mono"
            disabled={Boolean(editingId)}
            autoFocus={!editingId}
          />
        </Field>
        <Field label="Note *">
          <Input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            className="h-9"
            placeholder="e.g. Less Spicy"
            autoFocus={Boolean(editingId)}
          />
        </Field>
        <Field label="Group">
          <Select value={form.group} onValueChange={(v) => setField("group", v)}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DEFAULT_GROUPS.map((g) => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Sort Order">
            <Input
              type="number"
              value={form.sort_order}
              onChange={(e) => setField("sort_order", e.target.value)}
              className="h-9 text-right"
            />
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => setField("status", v)}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </div>
    </MasterCrudShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
